import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

// ─── Spinner ─────────────
function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function ReferralPage() {
  const queryClient = useQueryClient();
  const [convertAmount, setConvertAmount] = useState(10);
  const [copySuccess, setCopySuccess] = useState(false);

  // ── Fetch referral code ──
  const { data: codeData, isLoading: loadingCode } = useQuery({
    queryKey: ['referral-code'],
    queryFn: () => api.get('/referral/code').then((r) => r.data),
  });

  // ── Fetch balance ──
  const { data: balance, isLoading: loadingBalance } = useQuery({
    queryKey: ['referral-balance'],
    queryFn: () => api.get('/referral/balance').then((r) => r.data),
  });

  // ── Fetch invites ──
  const { data: invitesData } = useQuery({
    queryKey: ['referral-invites'],
    queryFn: () => api.get('/referral/invites').then((r) => r.data),
  });

  // ── Fetch history ──
  const { data: historyData } = useQuery({
    queryKey: ['referral-history'],
    queryFn: () => api.get('/referral/history').then((r) => r.data),
  });

  // ── Convert mutation ──
  const convertMutation = useMutation({
    mutationFn: (points: number) => api.post('/referral/convert', { points }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-balance'] });
      queryClient.invalidateQueries({ queryKey: ['referral-history'] });
    },
  });

  const copyToClipboard = () => {
    if (codeData?.share_url) {
      navigator.clipboard.writeText(codeData.share_url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (loadingCode || loadingBalance) return <Spinner />;

  const invites = invitesData?.invites ?? [];
  const history = historyData?.history ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900 dark:text-white">🤝 Referral</h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Invita amici nel Movimento del Dono e accumula punti convertibili
        </p>
      </div>

      {/* Codice referral + Share */}
      <div className="card p-6 bg-gradient-to-br from-primary-500/10 to-primary-600/5 dark:from-primary-500/20 dark:to-primary-600/10 border border-primary-200 dark:border-primary-800">
        <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-3">Il tuo codice referral</h2>
        <div className="flex items-center gap-3">
          <code className="text-3xl font-mono font-bold tracking-widest text-primary-600 dark:text-primary-400 bg-white dark:bg-[#1b1b1f] px-4 py-2 rounded-xl">
            {codeData?.referral_code ?? '...'}
          </code>
          <button
            onClick={copyToClipboard}
            className="btn-primary px-4 py-2 rounded-xl text-sm"
          >
            {copySuccess ? '✅ Copiato!' : '📋 Copia link'}
          </button>
        </div>
        {codeData?.share_url && (
          <p className="text-sm text-surface-500 dark:text-surface-400 mt-2 break-all">
            {codeData.share_url}
          </p>
        )}
      </div>

      {/* Bilancio Punti */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {balance?.available ?? 0}
          </div>
          <div className="text-sm text-surface-500 dark:text-surface-400">Punti disponibili</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {balance?.total_earned ?? 0}
          </div>
          <div className="text-sm text-surface-500 dark:text-surface-400">Punti guadagnati</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {balance?.total_spent ?? 0}
          </div>
          <div className="text-sm text-surface-500 dark:text-surface-400">Punti spesi</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            €{balance?.eur_value?.toFixed(2) ?? '0.00'}
          </div>
          <div className="text-sm text-surface-500 dark:text-surface-400">Valore EUR</div>
        </div>
      </div>

      {/* Conversione punti */}
      {(balance?.available ?? 0) > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-3">💱 Converti punti</h2>
          <p className="text-sm text-surface-500 dark:text-surface-400 mb-4">
            1 punto = €{((balance?.conversion_rate_cents ?? 50) / 100).toFixed(2)} — Converti in credito per ingressi City-Box
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={balance?.available ?? 0}
              value={convertAmount}
              onChange={(e) => setConvertAmount(Number(e.target.value))}
              className="input w-32"
            />
            <span className="text-surface-500 dark:text-surface-400">punti</span>
            <span className="text-surface-900 dark:text-white font-semibold">
              = €{(convertAmount * (balance?.conversion_rate_cents ?? 50) / 100).toFixed(2)}
            </span>
            <button
              onClick={() => convertMutation.mutate(convertAmount)}
              disabled={convertMutation.isPending || convertAmount <= 0 || convertAmount > (balance?.available ?? 0)}
              className="btn-primary px-4 py-2 rounded-xl text-sm disabled:opacity-50"
            >
              {convertMutation.isPending ? '...' : 'Converti'}
            </button>
          </div>
          {convertMutation.isError && (
            <p className="text-red-500 text-sm mt-2">
              {(convertMutation.error as any)?.response?.data?.error ?? 'Errore conversione'}
            </p>
          )}
        </div>
      )}

      {/* Invitati */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-3">
          👥 I tuoi invitati ({invites.length})
        </h2>
        {invites.length === 0 ? (
          <p className="text-surface-500 dark:text-surface-400 text-sm">
            Nessun invitato ancora. Condividi il tuo codice!
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 dark:border-surface-700">
                  <th className="text-left py-2 text-surface-500 dark:text-surface-400">Nome</th>
                  <th className="text-left py-2 text-surface-500 dark:text-surface-400">Email</th>
                  <th className="text-left py-2 text-surface-500 dark:text-surface-400">Stato</th>
                  <th className="text-left py-2 text-surface-500 dark:text-surface-400">Data</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv: any) => (
                  <tr key={inv.id} className="border-b border-surface-100 dark:border-surface-800">
                    <td className="py-2 text-surface-900 dark:text-white">{inv.full_name}</td>
                    <td className="py-2 text-surface-500 dark:text-surface-400">{inv.email}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        inv.status === 'joined'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}>
                        {inv.status === 'joined' ? 'Entrato' : 'Registrato'}
                      </span>
                    </td>
                    <td className="py-2 text-surface-500 dark:text-surface-400">
                      {new Date(inv.created_at).toLocaleDateString('it-IT')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Storico punti */}
      {history.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-3">📜 Storico punti</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between py-2 border-b border-surface-100 dark:border-surface-800">
                <div>
                  <span className="text-sm text-surface-900 dark:text-white capitalize">
                    {item.reason === 'registration' ? '📝 Registrazione invitato'
                      : item.reason === 'invited_joined' ? '�️ Invitato entrato'
                      : item.reason === 'conversion' ? '💱 Conversione punti'
                      : item.reason}
                  </span>
                  <span className="text-xs text-surface-400 dark:text-surface-500 ml-2">
                    {new Date(item.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
                <span className={`font-mono font-semibold ${
                  item.points > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                }`}>
                  {item.points > 0 ? '+' : ''}{item.points}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
